import { useEffect, useState, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Loading from '../components/Loading';
import UserProfile from '../ui/UserProfile';
import Logout from '../features/settings/logout/Logout';
import AccountDelete from '../features/settings/accountDelete/AccountDelete';
import PrivateLayout from '@/layouts/PrivateLayout';
import LanguagePreference from '../features/settings/language/LanguagePreference';
import TimeZonePreference from '../features/settings/timezone/TimeZonePreference';
import WeekStartPreference from '../features/settings/weekStart/WeekStartPreference';
import BirthYearPreference from '@/features/settings/birthYear/BirthYearPreference';
import MailStepDeliverySettings from '@/features/settings/mailStep/MailStepDeliverySettings';
import { apiGetRequest } from '@/utils/apiClient';
import ManageSubscriptionsButton from '@/ui/ManageSubscriptionsButton';
import { isReallyBlank } from '@/utils/utils';
import { useUser } from '@/hooks/UserContext';
import { useTranslation } from 'react-i18next';

// 型定義
export interface SettingsData {
    userId: number;
    accountName: string;
    email: string;
    subscriptionStatus: string;
    googleAccountDisplayNames: string[];
    googleAccountProviderIds: string[];
    microsoftAccountDisplayNames: string[];
    microsoftAccountProviderIds: string[];
    language?: string;
    locale?: string;
    timeZone?: string;
    birthYear?: number | null;
}

/**
 * 設定画面コンポーネント
 * @returns 設定画面
 */
function Settings(): ReactElement {
    // 関数系
    const navigate = useNavigate();
    const { isLoading: userLoading } = useUser();
    const { t } = useTranslation('settings');
    const pageTitle = t('meta.title');

    // 値を扱う変数
    const [username, setUsername] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [userId, setUserId] = useState<number>(0);
    const [subscriptionStatus, setSubscriptionStatus] = useState<string>('');

    const [isLoading, setIsLoading] = useState<boolean>(true);

    // メイン処理
    useEffect(() => {
        const init = async (): Promise<void> => {
            // UserContextの読み込み中は待機
            if (userLoading) return;

            // ローディング開始
            setIsLoading(true);

            try {
                // データ取得
                const response = await apiGetRequest('/api/settings');
                // エラーの場合
                if (!response.ok) {
                    // 401未認証エラーの場合
                    if (response.status === 401) {
                        // ログインページにリダイレクト
                        window.location.replace('/login?error=unauthorized');
                        return;
                    }
                    // その他エラーの場合（サーバーエラー）
                    window.location.replace('/login?error=server_error');
                    return;
                }

                // データ取得
                const data: SettingsData = await response.json();
                // パラメータセット
                setEmail(data.email);
                setUsername(data.accountName);
                setUserId(data.userId);
                setSubscriptionStatus(data.subscriptionStatus);
            } catch (_err) {
                // ログインページにリダイレクト（サーバーエラー）
                window.location.replace('/login?error=server_error');
                return;
            } finally {
                // ローディング終了
                setIsLoading(false);
            }
        };

        // 上記処理実行
        init();
    }, [navigate, userLoading]);

    // UserContextの読み込み中やSettingsのローディング中
    if (userLoading || isLoading) {
        return <Loading />;
    }

    /**
     * レンダリング
     * @returns レンダリング
     */
    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>

            <PrivateLayout>
                {/* メインコンテンツ */}
                <div className="w-full max-w-4xl flex flex-col p-4 sm:p-8 gap-8 mx-auto">
                    
                    {/* プロフィール */}
                    <section className="flex flex-col gap-4">
                        <h3 className="text-lg font-semibold text-gray-800">{t('group.profile')}</h3>
                        <div className="flex flex-col gap-6 bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                            <UserProfile username={username} email={email} />
                            <hr className="border-gray-100" />
                            <BirthYearPreference />
                        </div>
                    </section>

                    {/* アプリ設定 */}
                    <section className="flex flex-col gap-4">
                        <h3 className="text-lg font-semibold text-gray-800">{t('group.preferences')}</h3>
                        <div className="flex flex-col gap-6 bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                            <LanguagePreference />
                            <TimeZonePreference />
                            <WeekStartPreference />
                        </div>
                    </section>

                    {/* 通知・配信 */}
                    <section className="flex flex-col gap-4">
                        <h3 className="text-lg font-semibold text-gray-800">{t('group.notifications')}</h3>
                        <div className="flex flex-col gap-6 bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                            <MailStepDeliverySettings />
                        </div>
                    </section>

                    {/* サブスクリプション */}
                    <section className="flex flex-col gap-4">
                        <h3 className="text-lg font-semibold text-gray-800">{t('subscription.heading')}</h3>
                        <div className="flex flex-col gap-6 bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                            <div className="flex flex-col gap-2">
                                {!isReallyBlank(subscriptionStatus) ? (
                                    <ManageSubscriptionsButton subscriptionStatus={subscriptionStatus} />
                                ) : (
                                    <p className="text-sm text-gray-500">{t('subscription.empty')}</p>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* その他 */}
                    <section className="flex flex-col gap-4">
                        <h3 className="text-lg font-semibold text-gray-800">{t('group.dangerZone')}</h3>
                        <div className="flex flex-col gap-6 bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                            <div className="flex flex-col gap-2">
                                <h4 className="text-base font-medium text-gray-800">{t('logout.heading')}</h4>
                                <Logout />
                            </div>
                            <hr className="border-gray-100" />
                            <div className="flex flex-col gap-2">
                                <h4 className="text-base font-medium text-red-600">{t('accountDelete.heading')}</h4>
                                <AccountDelete userId={userId} />
                            </div>
                        </div>
                    </section>

                </div>
            </PrivateLayout>
        </>
    );
}

export default Settings;
